var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Schema, type } from '@colyseus/schema';
/**
 * What is lying on the floor, and what a client is told about it.
 *
 * The first thing in this game that exists in the world but is **not a body**.
 * A player and a monster are entities in `@d2/core`'s world — they walk, they
 * block, they can be swung at; a dropped item does none of that. It sits on a
 * cell until somebody takes it, which is why it is a map of its own here rather
 * than a third kind in `EntityKind`, and why nothing about it is written per
 * tick.
 *
 * ## The whole cost is two events, and neither of them repeats
 *
 * A monster costs twelve bytes a tick because it moves. **An item never moves.**
 * It is written once when it drops and deleted once when somebody picks it up,
 * so a floor covered in loot costs exactly nothing in the stream
 * `bandwidth.test.ts` guards — the same property that makes an idle party free,
 * applied to something that is idle by nature. What it does cost is the join
 * snapshot of a client arriving after a fight, and that is measured rather than
 * assumed (`bandwidth.test.ts`, "the loot on the floor").
 *
 * Every field below is chosen against that: a drop is a handful of bytes, once.
 *
 * ## A cell, not a position
 *
 * `cellX`/`cellY` are `int16`, the same type and the same reason as
 * `PlayerState.destinationX/Y`: a drop lies **on a cell**, so the wire says so,
 * and two bytes an axis is half what a `float32` costs. It is also exact — a
 * client and a server agreeing to the last bit about where a thing is, which is
 * what lets the server measure a pickup's range against the identical point the
 * client drew. A `float32` position would be a rounding of a number the server
 * holds in float64, and a reach measured against a rounded point is a reach the
 * two sides can disagree about at the boundary.
 *
 * Several things may lie on one cell — a kill leaves gear *and* a potion *and* a
 * pile of coins (`drops.ts`), and two kills can happen on one square. So the map
 * key is the id the room minted, never the cell, and a client fanning three
 * items out around a square is doing what a renderer is for.
 *
 * ## References, never tables — and never a name
 *
 * `baseId` is a row in `@d2/core`'s `ITEM_BASES`, `POTION_BASES` or
 * `RESPEC_SCROLL_BASES`, and `rarity` is an index into `ITEM_RARITY_IDS`. Both
 * sides hold core, so the client looks up what it needs to draw a sprite and
 * colour a label; this package restates neither table, exactly as `MonsterState`
 * sends a champion *code* and lets the client ask core what the tier does.
 *
 * **This is `TODO.md` #427's question, answered.** A ground item on this wire is
 * *a base id, plus a kind saying which of core's tables that id is a row in,
 * plus only what the kind alone cannot say* — a tier for gear, an amount for
 * gold. The respec scroll is what tested that answer from a second direction
 * (`TODO.md` #455) and it needed **no new field**: a third base table and a
 * fourth code, and the format was already the right shape. A kind that ever
 * needs a field of its own is the day this design is wrong, and there is now
 * evidence that it holds for more than the three it was written for.
 *
 * What is deliberately **absent** is the item itself. Its affixes, its rolled
 * modifiers, its price and its full name are not here: a magic item's name is a
 * prefix and a suffix the roll chose, and none of that is drawn on the ground —
 * D2 shows a label, and until an inventory is on the wire the label is the base
 * in the tier's colour (`TODO.md` #421). A field this package does not declare
 * cannot be sent by accident.
 *
 * ## Field order is the wire format
 *
 * `@colyseus/schema` numbers fields by declaration order and sends the number,
 * not the name. **Append only** — the same rule, and the same reason, as
 * `PlayerState` and `MonsterState`.
 */
/**
 * The four channels a kill can pay out in, in the order their codes are
 * assigned.
 *
 * They are `drops.ts`'s four channels seen from the wire: gear, potions, gold
 * and the respec scroll are rolled independently of one another and one kill can
 * leave all four. **Append only** — a code is a wire value.
 *
 * ## Why the scroll is a kind and not a potion
 *
 * `respec` was appended on 2026-08-13, when the channel core had been rolling
 * since 2026-08-12 turned out to have nowhere to land (`TODO.md` #455): the roll
 * fired, a token was minted, and nothing on this wire could name it.
 *
 * It could have ridden the `potion` code — both are a base with no roll on it —
 * and that would have been the same mistake `drops.ts` refused when it declined
 * to make the scroll one potion in twelve. A client switches on this code to
 * pick a sprite and a label, so a Token of Absolution drawn as a flask is a
 * label the player misreads, and *"a misclick from ending a character"*
 * (`docs/GAME-DESIGN.md`) is not a thing to leave a player guessing about. The
 * code is a fixed-width `uint8` every entry pays whatever it holds, so the
 * fourth value costs the wire **nothing at all** — see `GroundItemState.kind`.
 */
export const GROUND_KINDS = Object.freeze(['gear', 'potion', 'gold', 'respec']);
/** The kind code of an entry nobody described. Never valid; see `GroundItemState.kind`. */
export const NO_GROUND_KIND = 0;
/**
 * The rarity tiers, in the order their codes are assigned.
 *
 * Written out here rather than imported from `@d2/core`'s `RARITIES` for the
 * reason `MONSTER_ARCHETYPES` is: this is the *wire's* vocabulary, a code that
 * crosses the network has to keep meaning what it meant last release, and core's
 * table order is core's business. `ground.test.ts` holds the two lists together,
 * which is a comparison of two definitions rather than a tautology — the day
 * core reorders a tier, that test goes red and somebody decides what the wire
 * does about it. **Append only.**
 */
export const ITEM_RARITY_IDS = Object.freeze([
    'junk',
    'normal',
    'magic',
    'rare',
    'set',
    'unique',
]);
/** The rarity code of something that has no tier — a potion, a pile of coins, a respec scroll. */
export const NO_RARITY = 0;
/** The largest pile this wire can carry, which is what a `uint32` holds. */
export const MAX_PILE = 0xffff_ffff;
/** The bounds of the `int16` a cell is carried in. */
const MIN_CELL = -32_768;
const MAX_CELL = 32_767;
export class GroundItemState extends Schema {
    constructor() {
        super(...arguments);
        /**
         * The cell it lies on. `int16`, whole, exact on both sides — see the header.
         *
         * Signed for the same reason a destination is: a coordinate system that ever
         * admits a negative origin must not wrap silently.
         */
        this.cellX = 0;
        this.cellY = 0;
        /**
         * Which of the four channels this came out of (see `GROUND_KINDS`).
         *
         * `uint8`, one-based, with zero reserved for "nobody wrote this" — the same
         * reservation `MonsterState.archetype` makes and for the same reason: a schema
         * field starts at zero, so an entry that reached a client undescribed would
         * otherwise render as gear, silently and forever.
         *
         * **Fixed width, so a new kind is free.** A `uint8` costs the same byte
         * holding 4 as holding 1, which is why `respec` earned a code of its own
         * rather than being folded into `potion` to save space it would not have
         * saved. There are 251 codes left; the format runs out of *tables* long before
         * it runs out of room here.
         */
        this.kind = NO_GROUND_KIND;
        /**
         * The tier, as a code into `ITEM_RARITY_IDS`, or `NO_RARITY`.
         *
         * The colour of the label, which is the whole of what a player reads off the
         * floor before deciding to walk over — `docs/GAME-DESIGN.md` gives the six
         * tiers a palette for exactly this. Zero is "no tier at all", which is what a
         * potion, a pile of coins and a respec scroll are, so three of the four kinds
         * pay nothing for this field.
         */
        this.rarity = NO_RARITY;
        /**
         * The base this dropped from: a row in core's `ITEM_BASES`, `POTION_BASES` or
         * `RESPEC_SCROLL_BASES`.
         *
         * A reference rather than a name, so the client draws the right sprite and
         * reads the right label out of the table it already has. Empty for gold,
         * which has no base and costs nothing for saying so.
         *
         * The only variable-width field on an entry, so it is the whole of what one
         * drop costs over another — and the scroll is what currently sets the worst
         * case: `token-of-absolution` is two characters longer than the longest potion
         * id, which is the entire bandwidth consequence of the fourth kind
         * (`bandwidth.test.ts`, `MAX_BYTES_PER_DROP`).
         */
        this.baseId = '';
        /**
         * The coins in this pile, or zero for anything that is not one.
         *
         * On the wire because D2 shows the amount on the floor, and because a purse
         * that could only take part of a pile leaves the rest lying there
         * (`gold.ts`'s `overflow`) — so this is the one field of an entry that can
         * change while it lies there. `setPileAmount` is its only writer.
         */
        this.gold = 0;
    }
}
__decorate([
    type('int16'),
    __metadata("design:type", Object)
], GroundItemState.prototype, "cellX", void 0);
__decorate([
    type('int16'),
    __metadata("design:type", Object)
], GroundItemState.prototype, "cellY", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], GroundItemState.prototype, "kind", void 0);
__decorate([
    type('uint8'),
    __metadata("design:type", Object)
], GroundItemState.prototype, "rarity", void 0);
__decorate([
    type('string'),
    __metadata("design:type", Object)
], GroundItemState.prototype, "baseId", void 0);
__decorate([
    type('uint32'),
    __metadata("design:type", Object)
], GroundItemState.prototype, "gold", void 0);
/** The code for a kind. Throws on one the wire cannot name — the same line `createMonsterState` draws. */
const kindCode = (kind) => {
    const index = GROUND_KINDS.indexOf(kind);
    if (index < 0)
        throw new RangeError(`the wire cannot name the ground kind "${kind}"`);
    return index + 1;
};
/** The code for a rarity id. */
const rarityCode = (id) => {
    const index = ITEM_RARITY_IDS.indexOf(id);
    if (index < 0)
        throw new RangeError(`the wire cannot name the rarity "${id}"`);
    return index + 1;
};
const assertCell = (cell) => {
    const whole = Number.isInteger(cell.x) && Number.isInteger(cell.y);
    const carried = cell.x >= MIN_CELL && cell.x <= MAX_CELL && cell.y >= MIN_CELL && cell.y <= MAX_CELL;
    if (!whole || !carried) {
        throw new RangeError(`a drop at (${cell.x}, ${cell.y}) is not one this wire can place`);
    }
};
const assertPile = (gold) => {
    if (!Number.isInteger(gold) || gold < 0 || gold > MAX_PILE) {
        throw new RangeError(`a pile of ${gold} gold is not one this wire can carry`);
    }
};
/**
 * Builds a `GroundItemState`.
 *
 * Construction goes through a function for the reasons `createMonsterState`
 * exists — nothing depends on field order, and an entry is fully described the
 * moment it enters the map — including the one that matters most here: it is the
 * single place an *identity* becomes a wire code, so a base or a tier neither
 * side can name fails on the server that invented it rather than arriving as
 * something nobody can draw.
 *
 * A cell or a pile this wire cannot carry is malformed construction rather than
 * an ordinary outcome, so it throws: it is the server's own number, and a
 * silently wrapped `int16` would put somebody's loot on the far side of the map.
 */
export const createGroundItemState = (init) => {
    assertCell(init.cell);
    const state = new GroundItemState();
    state.cellX = init.cell.x;
    state.cellY = init.cell.y;
    state.kind = kindCode(init.kind);
    if (init.kind === 'gold') {
        assertPile(init.gold);
        state.gold = init.gold;
        return state;
    }
    // Everything that is not a pile names a base. Only gear has a tier to go with
    // it — a potion and a scroll are wholly described by which row they came from,
    // which is why neither pays for `rarity`.
    state.baseId = init.baseId;
    if (init.kind === 'gear')
        state.rarity = rarityCode(init.rarity);
    return state;
};
/**
 * Re-states what is left in a pile.
 *
 * The single writer of `gold` after construction, and the only mutation an entry
 * on the floor ever sees: a purse at its cap takes what fits and leaves the
 * remainder where it lay (`gold.ts` hands the overflow back for exactly this
 * decision, and D2 leaves it on the floor).
 */
export const setPileAmount = (state, gold) => {
    assertPile(gold);
    state.gold = gold;
};
/**
 * The kind a code names.
 *
 * Refuses an unknown code rather than guessing, exactly as `monsterArchetypeOf`
 * does: an entry with no kind is an entry the server failed to describe, and
 * drawing it as gear would hide that forever.
 */
export const groundKindOf = (code) => {
    const kind = GROUND_KINDS[code - 1];
    if (kind === undefined)
        throw new RangeError(`no ground kind has the code ${code}`);
    return kind;
};
/**
 * The rarity a code names, or null for something that has no tier.
 *
 * Null rather than a throw for zero, for the reason `championTierIdOf` answers
 * null: "no tier" is the ordinary answer for three of the four kinds and not a
 * failure at all.
 */
export const itemRarityIdOf = (code) => {
    if (code === NO_RARITY)
        return null;
    const id = ITEM_RARITY_IDS[code - 1];
    if (id === undefined)
        throw new RangeError(`no rarity has the code ${code}`);
    return id;
};
/** The cell an entry lies on, as core's own vocabulary — the client's half of the contract. */
export const groundCell = (state) => ({ x: state.cellX, y: state.cellY });
//# sourceMappingURL=ground.js.map